import { useEffect } from "react";
import { Col, Pagination, PaginationItem, PaginationLink, Row } from "reactstrap";
import usePagination from "../../hook/usePagination";
import { Pageable } from "../../types/common";
import LabelCol from "./label-col";

const PaginationEllipsis = (props: { visible?: boolean }) =>
  <PaginationItem disabled className={props.visible ? '' : 'd-none'}>
    <PaginationLink>...</PaginationLink>
  </PaginationItem>

interface PaginationBarProps<T> extends Pageable<T> {
  className?: string;
  displayLimit?: number; // how many pages to display in the navigation bar
  children?: React.ReactNode; // additional summary section
  onPageChange: (pagedData: T[]) => void;
  startPage?: number // if we want to force starting on a page other than the first one
}

const PaginationBar = <T,>(props: PaginationBarProps<T>) => {
  const { className, data, pageSize, displayLimit = 10, children, onPageChange, startPage = 1 } = props;

  const totalItems = data?.length ?? 0;

  const [pageCount, pagedData, pageNumber, setPageNumber] = usePagination(data, pageSize, startPage);

  useEffect(() => pagedData && onPageChange(pagedData), [pagedData, onPageChange]);

  // for navigation bar
  let pageButtonBegin = pageNumber - displayLimit / 2;
  let pageButtonEnd = pageNumber + displayLimit / 2;

  if (pageButtonBegin <= 0) {
    pageButtonBegin = 1;
    pageButtonEnd = Math.min(pageCount, displayLimit);
  }

  if (pageButtonEnd > pageCount) {
    pageButtonEnd = pageCount;
    pageButtonBegin = Math.max(pageCount - displayLimit + 1, 1);
  }

  if (pageButtonEnd - pageButtonBegin >= displayLimit) {
    pageButtonEnd -= 1;
  }

  // for summary info
  const itemsPerPage = pageSize ?? 0;
  const itemNumberBegin = (pageNumber - 1) * itemsPerPage + 1;
  const itemNumberEnd = Math.min(pageNumber * itemsPerPage, totalItems);

  return totalItems > 0 ? (
    <Row className="mt-2">
      <LabelCol className="oms-row">
        {totalItems > 0 && <>
          {pageCount > 1
            ? <span><b>Showing</b> {itemNumberBegin} - {itemNumberEnd} <b>of</b> {totalItems}</span>
            : <span><b>Total results:</b> {totalItems}</span>
          }
        </>}
        {children}
      </LabelCol>
      <Col>
        {pageCount > 1 &&
          <Pagination aria-label="Page navigation" className={className ?? 'd-flex justify-content-end'}>
            <PaginationItem disabled={pageNumber === 1}>
              <PaginationLink first disabled={pageNumber === 1}
                onClick={() => setPageNumber(1)} />
            </PaginationItem>
            <PaginationItem disabled={pageNumber === 1}>
              <PaginationLink previous disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)} />
            </PaginationItem>
            <PaginationEllipsis visible={pageButtonBegin > 1} />
            {
              Array.from({ length: pageCount }, (_, index) =>
                <PaginationItem key={index} active={pageNumber === index + 1}
                  className={index + 1 < pageButtonBegin || index + 1 > pageButtonEnd ? 'd-none' : ''}>
                  <PaginationLink disabled={pageNumber === index + 1}
                    onClick={() => setPageNumber(index + 1)}>
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              )
            }
            <PaginationEllipsis visible={pageButtonEnd < pageCount} />
            <PaginationItem disabled={pageNumber === pageCount}>
              <PaginationLink next disabled={pageNumber === pageCount}
                onClick={() => setPageNumber(pageNumber + 1)} />
            </PaginationItem>
            <PaginationItem disabled={pageNumber === pageCount}>
              <PaginationLink last disabled={pageNumber === pageCount}
                onClick={() => setPageNumber(pageCount)} />
            </PaginationItem>
          </Pagination>
        }
      </Col>
    </Row>
  ) : <></>;
};

export default PaginationBar;
